import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle, XCircle, Coffee, Users, UserCheck, Palmtree, UserX,
  FileEdit, FileText, CalendarHeart, Clock, AlertCircle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

export const AttendanceWidget = ({ currentAttendance, lastEvent, wsConnected }) => (
  <Card className="border-border/60 relative overflow-hidden" data-testid="attendance-widget">
    {wsConnected && (
      <div className="absolute top-3 right-3 flex items-center gap-1.5" data-testid="live-indicator">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
        </span>
        <span className="text-[10px] font-semibold tracking-wider uppercase text-green-600">Live</span>
      </div>
    )}
    <CardHeader className="border-b border-border/40 pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-green-600" />Aktuelle Anwesenheit
        </CardTitle>
        <Link to="/zeiterfassung/uebersicht"><Button variant="ghost" size="sm" className="text-primary">Details</Button></Link>
      </div>
    </CardHeader>
    <CardContent className="p-6">
      <div className="grid grid-cols-4 gap-3">
        <div className={`text-center p-3 rounded-lg bg-green-50 transition-all duration-500 ${lastEvent?.action === 'clock_in' ? 'ring-2 ring-green-400 scale-[1.02]' : ''}`}>
          <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-green-700 tabular-nums" data-testid="attendance-working">{currentAttendance.working}</p>
          <p className="text-xs text-green-600">Arbeitet</p>
        </div>
        <div className={`text-center p-3 rounded-lg bg-amber-50 transition-all duration-500 ${lastEvent?.action === 'break_start' || lastEvent?.action === 'break_end' ? 'ring-2 ring-amber-400 scale-[1.02]' : ''}`}>
          <Coffee className="h-5 w-5 text-amber-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-amber-700 tabular-nums" data-testid="attendance-break">{currentAttendance.on_break}</p>
          <p className="text-xs text-amber-600">Pause</p>
        </div>
        <div className={`text-center p-3 rounded-lg bg-gray-50 transition-all duration-500 ${lastEvent?.action === 'clock_out' ? 'ring-2 ring-gray-400 scale-[1.02]' : ''}`}>
          <XCircle className="h-5 w-5 text-gray-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-700 tabular-nums" data-testid="attendance-finished">{currentAttendance.finished}</p>
          <p className="text-xs text-gray-600">Beendet</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-red-50 transition-all duration-500">
          <Users className="h-5 w-5 text-red-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-red-700 tabular-nums" data-testid="attendance-absent">{currentAttendance.absent}</p>
          <p className="text-xs text-red-600">Nicht da</p>
        </div>
      </div>
      {lastEvent && (
        <div className="mt-4 flex items-center gap-2 text-sm bg-primary/5 border border-primary/10 rounded-lg px-3 py-2 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="live-event-ticker">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
          <span className="text-muted-foreground">
            <strong className="text-foreground">{lastEvent.employee_name}</strong> hat sich gerade <strong className="text-foreground">{lastEvent.action_label}</strong>
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

export const AbsencesTodayWidget = ({ stats }) => (
  <Card className="border-border/60" data-testid="absences-today-widget">
    <CardHeader className="border-b border-border/40 pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Palmtree className="h-5 w-5 text-teal-600" />Heute abwesend
        </CardTitle>
        <Link to="/team-kalender"><Button variant="ghost" size="sm" className="text-primary">Übersicht</Button></Link>
      </div>
    </CardHeader>
    <CardContent className="p-6">
      {((stats?.on_vacation_today?.length || 0) + (stats?.on_absence_today?.length || 0)) > 0 ? (
        <div className="space-y-3">
          {stats?.on_vacation_today?.map((item, idx) => (
            <div key={`vac-${item.employee_name}-${idx}`} className="flex items-center justify-between p-3 rounded-lg bg-teal-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                  <Palmtree className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-teal-900">{item.employee_name}</p>
                  <p className="text-xs text-teal-600">{item.vacation_type || 'Urlaub'}</p>
                </div>
              </div>
              <Badge className="bg-teal-100 text-teal-700">Urlaub</Badge>
            </div>
          ))}
          {stats?.on_absence_today?.map((item, idx) => (
            <div key={`abs-${item.employee_name}-${idx}`} className="flex items-center justify-between p-3 rounded-lg bg-orange-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <UserX className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-orange-900">{item.employee_name}</p>
                  <p className="text-xs text-orange-600">{item.absence_type || 'Abwesend'}</p>
                </div>
              </div>
              <Badge className="bg-orange-100 text-orange-700">{item.absence_type || 'Abwesend'}</Badge>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-muted-foreground">
          <UserCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Alle Mitarbeiter verfügbar</p>
        </div>
      )}
    </CardContent>
  </Card>
);

export const CorrectionsWidget = ({ stats }) => (
  <Card className="border-border/60" data-testid="corrections-widget">
    <CardHeader className="border-b border-border/40 pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <FileEdit className="h-5 w-5 text-orange-600" />Korrekturanträge
        </CardTitle>
        <Link to="/zeiterfassung/korrekturen"><Button variant="ghost" size="sm" className="text-primary">Alle anzeigen</Button></Link>
      </div>
    </CardHeader>
    <CardContent className="p-6">
      {(stats?.pending_corrections || 0) > 0 ? (
        <div className="flex items-center justify-between p-4 rounded-lg bg-orange-50">
          <div>
            <p className="text-3xl font-bold text-orange-700">{stats.pending_corrections}</p>
            <p className="text-sm text-orange-600">offene Anträge warten auf Prüfung</p>
          </div>
          <Link to="/zeiterfassung/korrekturen"><Button size="sm">Jetzt prüfen</Button></Link>
        </div>
      ) : (
        <div className="text-center py-4 text-muted-foreground">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Keine offenen Korrekturanträge</p>
        </div>
      )}
    </CardContent>
  </Card>
);

export const ChangeRequestsWidget = ({ pendingCount }) => (
  <Card className="border-border/60" data-testid="change-requests-widget">
    <CardHeader className="border-b border-border/40 pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600" />Änderungsanträge
        </CardTitle>
        <Link to="/aenderungsantraege"><Button variant="ghost" size="sm" className="text-primary">Alle anzeigen</Button></Link>
      </div>
    </CardHeader>
    <CardContent className="p-6">
      {pendingCount > 0 ? (
        <div className="flex items-center justify-between p-4 rounded-lg bg-blue-50">
          <div>
            <p className="text-3xl font-bold text-blue-700">{pendingCount}</p>
            <p className="text-sm text-blue-600">Mitarbeiter-Änderungsanträge offen</p>
          </div>
          <Link to="/aenderungsantraege"><Button size="sm">Jetzt prüfen</Button></Link>
        </div>
      ) : (
        <div className="text-center py-4 text-muted-foreground">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Keine offenen Änderungsanträge</p>
        </div>
      )}
    </CardContent>
  </Card>
);

export const HolidaysWidget = ({ holidays }) => (
  <Card className="border-border/60" data-testid="holidays-widget">
    <CardHeader className="border-b border-border/40 pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <CalendarHeart className="h-5 w-5 text-purple-600" />Kommende Feiertage
        </CardTitle>
        <Link to="/feiertage"><Button variant="ghost" size="sm" className="text-primary">Alle anzeigen</Button></Link>
      </div>
    </CardHeader>
    <CardContent className="p-6">
      <div className="space-y-3">
        {holidays.map((h) => (
          <div key={`${h.date}-${h.name}`} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <div>
              <p className="font-medium">{h.name}</p>
              <p className="text-sm text-muted-foreground">{format(parseISO(h.date), 'EEEE, dd.MM.yyyy', { locale: de })}</p>
            </div>
            <Badge variant="outline" className="text-xs">{h.branch_name}</Badge>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const PendingRequestsWidget = ({ requests, formatDate, getStatusIcon, getStatusLabel }) => (
  <Card className="border-border/60">
    <CardHeader className="border-b border-border/40 pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-amber-600" />Offene Urlaubsanträge
        </CardTitle>
        <Link to="/vacation-requests"><Button variant="ghost" size="sm" className="text-primary">Alle anzeigen</Button></Link>
      </div>
    </CardHeader>
    <CardContent className="p-6">
      {requests?.length > 0 ? (
        <div className="space-y-4">
          {requests.map((request, index) => (
            <div key={request.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 animate-slide-up" style={{ animationDelay: `${index * 0.05}s` }}>
              <div className="flex items-center gap-3">
                {getStatusIcon(request.status)}
                <div>
                  <p className="font-medium">{request.employee_name}</p>
                  <p className="text-sm text-muted-foreground">{formatDate(request.start_date)} - {formatDate(request.end_date)}</p>
                </div>
              </div>
              <Badge className="status-pending">{getStatusLabel(request.status)}</Badge>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Keine offenen Anträge</p>
        </div>
      )}
    </CardContent>
  </Card>
);
